import {useEffect} from 'react';

import {documentToHtmlString} from '@contentful/rich-text-html-renderer';
import Image from 'next/image';
import {useRouter} from 'next/router';
import {FormattedMessage} from 'react-intl';
import styled from 'styled-components';

import {ActionButton, dimensions} from '@stryd/react-ui';
import {stripeAmountToCurrencyAmount} from '@stryd/util-lib';

import {regionConfigs} from 'src/config';
import {usePageRegionData} from 'src/contexts/PageRegionDataContext';
import {useCart} from 'src/features/cart/contexts';
import {CartActionItem} from 'src/features/cart/types';
import {getVariantPricing} from 'src/features/pricing/helpers';
import {dimensions as customDimensions} from 'src/styles';
import {colors as customColors} from 'src/styles/colors';
import {addToCartEvent, viewItemEvent} from 'src/utils/analytics/events';
import {track} from 'src/utils/analytics/gtag';
import {variantToItems} from 'src/utils/analytics/variantToItems';
import {IProduct, IProductVariant} from 'src/utils/contentful';
import {getImageSourceFromAsset} from 'src/utils/contentful/content-models/util';

import {FullWidthSection} from '../FullWidthSection';
import {ProductVariantPrice} from '../ProductVariantPrice';

const ProductContentSection = styled(FullWidthSection)`
  background-color: ${customColors.backgroundLight};
  min-height: 100vh;
`;

const GridContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const columnLayoutWidth = dimensions.breakpoints.tabletLandscape;
const gridGap = dimensions.twicePadding;

const Grid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${gridGap};
  justify-items: center;
  width: 100%;
  max-width: 1100px;

  @media screen and (max-width: ${columnLayoutWidth}) {
    grid-template-columns: 1fr;
  }
`;

const StickyPhotoContainer = styled.div`
  width: 100%;
  padding-top: 100%;
  height: 0px;

  border-radius: ${dimensions.defaultBorderRadius};
  overflow: hidden;

  position: sticky;
  top: calc(
    ${dimensions.topNavHeight} + ${customDimensions.defaultPageVerticalPadding}
  );

  @media screen and (max-width: ${columnLayoutWidth}) {
    position: relative;
    top: unset;
  }
`;

const PhotoPositionContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
`;

const PhotoContainerPositionReset = styled.div`
  position: relative;

  width: 100%;
  height: 100%;
`;

const PhotoContainer = styled.div`
  position: relative;

  width: 100%;
  height: 100%;
  border-radius: ${dimensions.defaultBorderRadius};
  overflow: hidden;

  z-index: 0;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: calc(2 * ${gridGap});

  @media screen and (max-width: ${columnLayoutWidth}) {
    margin-top: ${gridGap};
  }
`;

const InfoContainerInner = styled.div`
  max-width: 500px;

  > * + * {
    margin-top: calc(2 * ${gridGap});
  }
`;

const ProductName = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ProductDescriptionContainer = styled.div`
  margin-bottom: ${dimensions.defaultPadding};
`;

const ProductPrice = styled.p`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

export interface ProductPageTemplateProps {
  product: IProduct;
  productVariant: IProductVariant;
}

export const ProductPageTemplate: React.FC<
  React.PropsWithChildren<ProductPageTemplateProps>
> = (props) => {
  const {product, productVariant} = props;
  const [, cartDispatch] = useCart();
  const {region, locale} = usePageRegionData();
  const router = useRouter();

  const {currency} = regionConfigs[region];

  const {
    fields: {images, previewImage, description, fullName},
  } = productVariant;

  const initialImage = images ? images[0] : previewImage;
  const initialImageAlt = initialImage.fields.title;
  const variantDescription = description || product.fields.description;

  const price = getVariantPricing({
    variant: productVariant,
    region,
    currency,
  });

  const addItemToCart = () => {
    const item: CartActionItem = {
      product,
      selectedVariant: productVariant,
    };
    cartDispatch({type: 'ADD_QTY_TO_CART', quantity: 1, item, region});

    track(
      addToCartEvent({
        ecommerce: {
          items: variantToItems(productVariant),
          currency: price.oneTimePricesTotal.currency,
          value: stripeAmountToCurrencyAmount(
            price.oneTimePricesTotal.unit_amount,
            price.oneTimePricesTotal.currency
          ), // TODO update this value to include possible subscription values
        },
      })
    );

    router.push({
      pathname: '/[region]/[locale]/cart',
      query: {region, locale},
    });
  };

  useEffect(() => {
    track(
      viewItemEvent({
        ecommerce: {
          items: variantToItems(productVariant),
          currency: price.oneTimePricesTotal.currency,
          value: stripeAmountToCurrencyAmount(
            price.oneTimePricesTotal.unit_amount,
            price.oneTimePricesTotal.currency
          ), // TODO update this value to include possible subscription values
        },
      })
    );
  }, [
    product,
    productVariant,
    price.oneTimePricesTotal.unit_amount,
    price.oneTimePricesTotal.currency,
  ]);

  return (
    <ProductContentSection>
      <GridContainer>
        <Grid>
          <StickyPhotoContainer>
            <PhotoPositionContainer>
              <PhotoContainerPositionReset>
                <PhotoContainer>
                  <Image
                    src={getImageSourceFromAsset(initialImage)}
                    alt={initialImageAlt}
                    layout="fill"
                    objectFit="cover"
                  />
                </PhotoContainer>
              </PhotoContainerPositionReset>
            </PhotoPositionContainer>
          </StickyPhotoContainer>
          <InfoContainer>
            <InfoContainerInner>
              <div>
                <ProductName>{fullName}</ProductName>
                <ProductPrice>
                  <ProductVariantPrice variant={productVariant} />
                </ProductPrice>
                <ActionButton onClick={addItemToCart}>
                  <FormattedMessage
                    id="actions.addToCart"
                    defaultMessage="Add to Cart"
                  />
                </ActionButton>
              </div>

              {variantDescription ? (
                <ProductDescriptionContainer
                  dangerouslySetInnerHTML={{
                    __html: documentToHtmlString(variantDescription),
                  }}
                />
              ) : null}
            </InfoContainerInner>
          </InfoContainer>
        </Grid>
      </GridContainer>
    </ProductContentSection>
  );
};
